/**
 * Top-level routing (§7g navigation refactor).
 *
 * v1 was persona-first: `/` was the persona grid, `/chat/:personaId`
 * was the single-persona chat. 7g shifts to project-first:
 *
 *   /                                  — project list home
 *   /projects/:projectId               — project view (tabs)
 *   /projects/:projectId/chat/:personaId/:conversationId?
 *                                      — chat inside a project context
 *   /projects/:projectId/roundtable/:conversationId?
 *                                      — roundtable inside a project (7h)
 *   /coaches                           — standalone persona grid (v1 preserved)
 *   /coaches/chat/:personaId/:conversationId?
 *                                      — standalone chat (v1 behaviour)
 *   /coaches/roundtable/:conversationId?
 *                                      — standalone roundtable (7h)
 *
 * Legacy /chat/:personaId/:conversationId? URLs from v1 bookmarks
 * redirect to /coaches/chat/... so the old muscle memory keeps working
 * even though the underlying route has moved.
 *
 * Project view tabs are nested via Outlet so the shell (header, tab
 * nav) doesn't re-mount when switching between Conversations and
 * Documents — fleshed out in Commit E.
 */

import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import Login from "./pages/Login.jsx";
import Callback from "./pages/Callback.jsx";
import ProjectList from "./pages/ProjectList.jsx";
import ProjectView from "./pages/ProjectView.jsx";
import ProjectOverviewTab from "./pages/ProjectOverviewTab.jsx";
import ProjectConversationsTab from "./pages/ProjectConversationsTab.jsx";
import ProjectDocumentsTab from "./pages/ProjectDocumentsTab.jsx";
import ProjectJobsTab from "./pages/ProjectJobsTab.jsx";
import Coaches from "./pages/Coaches.jsx";
import Chat from "./pages/Chat.jsx";
import Editor from "./pages/Editor.jsx";
import Roundtable from "./pages/Roundtable.jsx";
import Settings from "./pages/Settings.jsx";
import AdminLayout from "./pages/AdminLayout.jsx";
import AdminPersonas from "./pages/AdminPersonas.jsx";
import AdminPersonaEdit from "./pages/AdminPersonaEdit.jsx";
import AdminPrompts from "./pages/AdminPrompts.jsx";
import AdminPromptEdit from "./pages/AdminPromptEdit.jsx";
import AdminUsage from "./pages/AdminUsage.jsx";
import AdminGuardrails from "./pages/AdminGuardrails.jsx";
import AdminTenantSettings from "./pages/AdminTenantSettings.jsx";
import SharedProjectView from "./pages/SharedProjectView.jsx";
import AdminUsers from "./pages/AdminUsers.jsx";
import AdminAgentsList from "./pages/AdminAgentsList.jsx";
import AdminAgentsEdit from "./pages/AdminAgentsEdit.jsx";
import AdminAgentsNew from "./pages/AdminAgentsNew.jsx";
import AdminAgentsPending from "./pages/AdminAgentsPending.jsx";
import AdminTemplatesList from "./pages/AdminTemplatesList.jsx";
import AdminTemplateEdit from "./pages/AdminTemplateEdit.jsx";
import AdminTemplateNew from "./pages/AdminTemplateNew.jsx";
import TenantsList from "./pages/TenantsList.jsx";
import TenantDetail from "./pages/TenantDetail.jsx";
import TenantNew from "./pages/TenantNew.jsx";
import RequireAuth from "./components/RequireAuth.jsx";
import AdminGuard from "./components/AdminGuard.jsx";
import SetupRouteGuard from "./components/SetupRouteGuard.jsx";
import ToastContainer from "./components/ToastContainer.jsx";
import TenantSetupPage from "./pages/setup/TenantSetupPage.jsx";
import ProfileEditor from "./pages/setup/ProfileEditor.jsx";
import CatalogueEditor from "./pages/setup/CatalogueEditor.jsx";
import ActivatePanel from "./pages/setup/ActivatePanel.jsx";
import SetupPendingPlaceholder from "./pages/setup/SetupPendingPlaceholder.jsx";

export default function App() {
  return (
    <div className="app">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/callback" element={<Callback />} />
        {/* v4.1 Phase 37.5 — share-link viewer. Handles its own auth
            redirect so callers without a session get the "sign in or
            sign up with your org email" nudge automatically. */}
        <Route path="/share/:token" element={<SharedProjectView />} />

        {/* All authed routes share the RequireAuth + SetupRouteGuard
            stack. RequireAuth validates the JWT; SetupRouteGuard
            (Phase 44.3) redirects based on tenant status — tenants in
            setup_review push admins to /setup and members to
            /setup-pending; active tenants visiting /setup/* bounce
            back to home. Super-admins are exempt. */}
        <Route
          element={
            <RequireAuth>
              <SetupRouteGuard>
                <Outlet />
              </SetupRouteGuard>
            </RequireAuth>
          }
        >
          {/* Tenant-admin setup flow (Phase 44) */}
          <Route path="/setup" element={<TenantSetupPage />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileEditor />} />
            <Route path="catalogue" element={<CatalogueEditor />} />
            <Route path="activate" element={<ActivatePanel />} />
          </Route>
          <Route path="/setup-pending" element={<SetupPendingPlaceholder />} />

          {/* Project-first navigation */}
          <Route path="/" element={<ProjectList />} />

          {/* Project view with nested tabs (conversations / documents) */}
          <Route path="/projects/:projectId" element={<ProjectView />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<ProjectOverviewTab />} />
            <Route
              path="conversations"
              element={<ProjectConversationsTab />}
            />
            <Route path="documents" element={<ProjectDocumentsTab />} />
            <Route path="jobs" element={<ProjectJobsTab />} />
          </Route>

          {/* Project-scoped editor (§15.4) */}
          <Route
            path="/projects/:projectId/editor"
            element={<Editor />}
          />

          {/* Project-scoped chat */}
          <Route
            path="/projects/:projectId/chat/:personaId"
            element={<Chat />}
          />
          <Route
            path="/projects/:projectId/chat/:personaId/:conversationId"
            element={<Chat />}
          />

          {/* Project-scoped roundtable (§7h) */}
          <Route
            path="/projects/:projectId/roundtable"
            element={<Roundtable />}
          />
          <Route
            path="/projects/:projectId/roundtable/:conversationId"
            element={<Roundtable />}
          />

          {/* Standalone persona grid + chat (v1 preservation) */}
          <Route path="/coaches" element={<Coaches />} />
          <Route path="/coaches/chat/:personaId" element={<Chat />} />
          <Route
            path="/coaches/chat/:personaId/:conversationId"
            element={<Chat />}
          />

          {/* Standalone roundtable (§7h) */}
          <Route path="/coaches/roundtable" element={<Roundtable />} />
          <Route
            path="/coaches/roundtable/:conversationId"
            element={<Roundtable />}
          />

          {/* Account settings */}
          <Route path="/settings" element={<Settings />} />

          {/* Admin panel (§27) — gated by Cognito `admin` group membership.
              Non-admins bounce back to the project list. Foundation PR
              wires the layout shell + personas list only; other admin
              pages land in follow-ups. */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }
          >
            <Route index element={<Navigate to="personas" replace />} />
            <Route path="personas" element={<AdminPersonas />} />
            <Route path="personas/new" element={<AdminPersonaEdit />} />
            <Route path="personas/:id/edit" element={<AdminPersonaEdit />} />
            <Route path="prompts" element={<AdminPrompts />} />
            <Route path="prompts/:agentId/edit" element={<AdminPromptEdit />} />
            <Route path="usage" element={<AdminUsage />} />
            <Route path="guardrails" element={<AdminGuardrails />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="agents" element={<AdminAgentsList />} />
            <Route path="agents/new" element={<AdminAgentsNew />} />
            <Route path="agents/pending-prs" element={<AdminAgentsPending />} />
            <Route path="agents/:agentId/edit" element={<AdminAgentsEdit />} />
            <Route path="templates" element={<AdminTemplatesList />} />
            <Route path="templates/new" element={<AdminTemplateNew />} />
            <Route path="templates/:templateId/edit" element={<AdminTemplateEdit />} />
            <Route path="tenants" element={<TenantsList />} />
            <Route path="tenants/new" element={<TenantNew />} />
            <Route path="tenants/:tenantId" element={<TenantDetail />} />
            <Route path="tenant-settings" element={<AdminTenantSettings />} />
          </Route>

          {/* Legacy /chat/:personaId redirect — preserves v1 bookmarks */}
          <Route path="/chat/:personaId" element={<LegacyChatRedirect />} />
          <Route
            path="/chat/:personaId/:conversationId"
            element={<LegacyChatRedirect />}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
    </div>
  );
}

/**
 * Maps old `/chat/:personaId/:conversationId?` URLs onto the new
 * `/coaches/chat/...` path so existing bookmarks don't 404.
 */
function LegacyChatRedirect() {
  const { personaId, conversationId } = useParams();
  const dest = conversationId
    ? `/coaches/chat/${personaId}/${conversationId}`
    : `/coaches/chat/${personaId}`;
  return <Navigate to={dest} replace />;
}
